import React, { useState } from 'react';
import '../../App.css';
import './ContactPage.css';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../translations';

function ContactPage() {
  const { language } = useLanguage();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    newsletter: false
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [subscribedNewsletter, setSubscribedNewsletter] = useState(false);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'contactMessages'), {
        name: contactForm.name || '',
        email: contactForm.email || '',
        phone: contactForm.phone || '',
        message: contactForm.message || '',
        newsletterOptIn: !!contactForm.newsletter,
        status: 'new',
        createdAt: serverTimestamp()
      });
      setSubscribedNewsletter(!!contactForm.newsletter);
      setContactSubmitted(true);
      setContactForm({ name: '', email: '', phone: '', message: '', newsletter: false });
      setTimeout(() => setContactSubmitted(false), 7000);
    } catch (err) {
      console.error('Failed to submit contact message:', err);
      alert('Sorry, there was a problem sending your message. Please try again later.');
    }
  };

  return (
    <div className="info-page">
      {/* Single halftone band spanning both columns */}
      <section className="contact-band contact-band--fullwidth">
        <div className="info-columns halftone-columns">
          {/* ===== Left Column ===== */}
          <div className="info-column">
            <div className="contact-band__inner">
              <h2>{t('contact.contactUs', language)}</h2>

              <div className="contact-form-wrapper compact">
                {contactSubmitted ? (
                  <div className="success-message">
                    {t(subscribedNewsletter ? 'contact.thankYouNewsletter' : 'contact.thankYou', language)}
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="contact-form" noValidate>
                    <div className="form-group">
                      <input
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={handleContactChange}
                        placeholder={t('contact.namePlaceholder', language)}
                        required
                        autoComplete="name"
                      />
                    </div>

                    <div className="form-group">
                      <input
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={handleContactChange}
                        placeholder={t('contact.emailPlaceholder', language)}
                        required
                        autoComplete="email"
                      />
                    </div>

                    <div className="form-group">
                      <input
                        type="tel"
                        name="phone"
                        value={contactForm.phone}
                        onChange={handleContactChange}
                        placeholder={t('contact.phonePlaceholder', language)}
                        required
                        autoComplete="tel"
                      />
                    </div>

                    <div className="form-group">
                      <textarea
                        name="message"
                        value={contactForm.message}
                        onChange={handleContactChange}
                        placeholder={t('contact.messagePlaceholder', language)}
                        rows="3"
                      />
                    </div>

                    <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="newsletter-optin"
                        checked={contactForm.newsletter}
                        onChange={(e) => setContactForm((prev) => ({ ...prev, newsletter: e.target.checked }))}
                        style={{ width: 'auto', margin: '3px 0 0 0', flexShrink: 0 }}
                      />
                      <label htmlFor="newsletter-optin" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                        {t('newsletter.optIn', language)}
                      </label>
                    </div>

                    <button type="submit" className="submit-btn">
                      {t('contact.send', language)}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* ===== Right Column ===== */}
          <div className="info-column">
            <div className="contact-band__inner">
              <h2>{t('contact.gettingHere', language)}</h2>

              <div className="map-wrapper">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2246.7!2d13.2289!3d56.0734!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4651532c8f8f8f8f%3A0x0!2sBonnarp+645%2C+264+53+Ljungbyhed%2C+Sweden!5e0!3m2!1sen!2sse!4v1234567890123"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Idea Farm Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content below halftone band */}
      <div className="info-columns">
        {/* ===== Left Column ===== */}
        <div className="info-column">
          {/* FAQ (no halftone) */}
          <div className="faq-section">
            <p className="faq-header">{t('contact.faq', language)}</p>

            <div className="faq-category">
              <p className="faq-header">{t('contact.faqEvents', language)}</p>

              <div className="faq-category">
                <p className="faq-subheader"><b>{t('contact.faqPayDoor', language)}</b></p>
                <p>{t('contact.faqPayDoorA', language)}</p>
              </div>

              <div className="faq-category">
                <p className="faq-subheader"><b>{t('contact.faqWhatTime', language)}</b></p>
                <p>{t('contact.faqWhatTimeA1', language)}</p>
                <p>{t('contact.faqWhatTimeA2', language)}</p>
              </div>

              <div className="faq-category">
                <p className="faq-subheader"><b>{t('contact.faqPrintedTicket', language)}</b></p>
                <p>{t('contact.faqPrintedTicketA', language)}</p>
              </div>

              <div className="faq-category">
                <p className="faq-subheader"><b>{t('contact.faqAgeLimit', language)}</b></p>
                <p>{t('contact.faqAgeLimitA', language)}</p>
              </div>

              <div className="faq-category">
                <p className="faq-subheader"><b>{t('contact.faqAllergies', language)}</b></p>
                <p>{t('contact.faqAllergiesA', language)}</p>
              </div>

              <div className="faq-category">
                <p className="faq-subheader"><b>{t('contact.faqRefunds', language)}</b></p>
                <p>{t('contact.faqRefundsA1', language)}</p>
                <p>{t('contact.faqRefundsA2', language)}</p>
                <p>{t('contact.faqRefundsA3', language)}</p>
                <p>{t('contact.faqRefundsA4', language)}</p>
              </div>
            </div>

            <div className="faq-category">
              <p className="faq-subheader">{t('contact.accessibility', language)}</p>
              <p>{t('contact.accessibilityA1', language)}</p>
              <p>{t('contact.accessibilityA2', language)}</p>
              <p>{t('contact.accessibilityA3', language)}</p>
            </div>
          </div>
        </div>

        {/* ===== Right Column ===== */}
        <div className="info-column">
          {/* Address and transport info - outside halftone band */}
          <div className="address-transport-section">
            <div className="contact-details">
              <h3>{t('contact.address', language)}</h3>
              <p><strong>Idea Farm</strong></p>
              <p>Bonnarp 645</p>
              <p>264 53 Ljungbyhed</p>
              <p>{t('contact.sweden', language)}</p>
            </div>

            <div className="transport-section">
              <h3>{t('contact.byRoad', language)}</h3>
              <p className="important-notice">
                <strong>IMPORTANT</strong> {t('contact.parkingNotice', language)}
              </p>
            </div>

            <div className="transport-section">
              <h3>{t('contact.publicTransport', language)}</h3>
              <p>{t('contact.trainTo', language)}</p>
              <p>{t('contact.busRoute', language)}</p>
              <p>{t('contact.walkGolf', language)}</p>
              <p><strong>{t('contact.minibus', language)}</strong></p>
            </div>
          </div>
        </div>
      </div>

      {/* Bio Section with halftone background */}
      <section className="contact-band contact-band--fullwidth bio-section">
        <div className="info-columns halftone-columns">
          {/* Elsa's Bio */}
          <div className="info-column">
            <h2 className="bio-section-header">{t('contact.aboutUs', language)}</h2>
            <div className="bio-panel">
              <div className="bio-image">
                <img src="/images/elsa.png" alt="Elsa Qvarsell" />
              </div>
              <div className="bio-content">
                <h3>Elsa Qvarsell</h3>
                <p><b>Elsa Qvarsell</b>{t('contact.elsaBio1', language)}</p>
                <p>{t('contact.elsaBio2', language)}</p>
                <p>{t('contact.elsaBio3', language)}</p>
              </div>
            </div>
          </div>

          {/* Robbie's Bio */}
          <div className="info-column">
            <h2 className="bio-section-header bio-spacer">&nbsp;</h2>
            <div className="bio-panel">
              <div className="bio-image">
                <img src="/images/robbie_studio.jpg" alt="Robbie Moore" />
              </div>
              <div className="bio-content">
                <h3>Robbie Moore</h3>
                <p>{t('contact.robbieBio1', language)}</p>
                <p>{t('contact.robbieBio2', language)}</p>
                <p>{t('contact.robbieBio3', language)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
